/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Styled of Table component
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import styled, { keyframes } from 'styled-components';

import { IAttrStyled } from '@/main/types/globalTypes';
import { icons } from '@assets/index';
import { colors, globalThemeConstants } from '@theme/index';

import GlobalContainer from '../../common/GlobalContainer/GlobalContainer';
import { LinkContainerStyled } from '../../common/LinkBlock/styled/LinkStyled';
import { TABLE_FOOTER_BOTTOM_STYLE } from '../constants';

interface ITableContainerStyled extends IAttrStyled {
    className: string;
    footerHeight: number;
    rowPadding: number;
}

const TableContainerStyled = styled.div.attrs(({ className }: ITableContainerStyled) => ({
    className: `table ${className}`,
}))<ITableContainerStyled & IAttrStyled>`
    position: relative;
    width: 100%;

    // hide content but save container height
    .rt-tbody.hidden {
        visibility: hidden;
    }

    &.table-no-data .ReactTable {
        margin-bottom: 48px;
    }

    .ReactTable {
        border: none;

        .rt-table {
            overflow-y: hidden;
            overflow-x: hidden;
        }

        .rt-table:before {
            content: ' ';
            position: absolute;
            left: 200px;
            height: 100%;
            box-shadow: 5px 0 5px -5px #333;
        }

        .rt-thead {
            user-select: none;
        }

        .rt-thead,
        .rt-tfooter {
            overflow-x: hidden;
            overflow-y: scroll;
            -ms-scrollbar-arrow-color: ${colors.tableHeaderBgColor};

            &.-header {
                box-shadow: none;
                background-color: ${colors.tableHeaderBgColor};
            }

            .rt-resizable-header:last-child {
                overflow: visible;
            }

            .rt-resizable-header-content {
                overflow: unset;
            }

            .rt-th .filter {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                height: 24px;
            }

            .rt-th .filter .filter__title {
                width: 100%;
            }

            .rt-th {
                border: none;
                border-top: 1px solid ${colors.tableBorderTopColor};
                border-right: solid 1px transparent;
                border-bottom: solid 1px ${colors.tableGridBorderColor};
                white-space: normal;
                line-height: 1.6;
                align-self: flex-end;
                padding: 8px;

                .select__select-item {
                    text-align: left;
                }

                .select,
                .rt-th .input__container,
                .rt-th .drop-down-filter {
                    position: static;
                }
            }

            &:hover {
                .rt-th:not(.rthfc-th-fixed) {
                    border-right: solid 1px ${colors.tableHeadGhostBorderColorHover};
                }

                .rt-th:last-child {
                    border-right: solid 1px transparent;
                }
            }

            .rt-tr {
                text-align: left;
            }
        }

        .rt-tbody {
            overflow-x: hidden;
            overflow-y: auto;
            height: calc(100% - 80px);

            .rt-td {
                border-right: none;
            }

            .rt-tr-group {
                border-bottom: none;
            }

            .rt-tr-group .rt-tr-checked .rt-td {
                background-color: ${colors.tableCellBgColorChecked};
            }

            .rt-tr-group .rt-tr-delete {
                align-items: center;
                justify-content: center;
            }

            .rt-tr-group:hover .rt-td {
                background: ${colors.tableCellBgColorHover};
            }
        }

        .rt-tbody.with-footer {
            height: calc(100% - 125px);
        }

        .rt-tfooter .rt-tr .rt-td {
            background-color: ${colors.tableFooterTotalBgColor};
            height: 45px;
            font-weight: bold;
        }

        .rt-tr-group {
            flex: 0 1 auto;
        }

        .rt-thead .rt-th,
        .rt-tfooter .rt-td,
        .rt-tbody .rt-td {
            border-bottom: solid 1px ${colors.tableGridBorderColor};
            box-sizing: border-box;
            position: relative;
            /**
                Important is needed to override React Table styles.
                Max-width is written in element.style, so classes cannot be overridden
            */
            flex-basis: 440px !important;
            flex-grow: 0 !important;
        }

        .rt-thead .rt-th {
            padding: 8px;
        }

        .rt-tbody .rt-td,
        .rt-tfooter .rt-td {
            padding: ${(props) => props.rowPadding || 10}px 8px;
        }

        .rt-tbody .rt-td[data-col-name='unpaidBillsCount'] {
            .block-with-icon {
                display: flex;
                flex-direction: row;

                &__icon-left {
                    box-sizing: content-box;
                    width: 13px;
                    height: 13px;
                    padding: 0 6px 0 0;
                    margin-top: -3px;
                }
            }
        }

        .rt-thead .rt-th.rthfc-th-fixed-left-last,
        .rt-tbody .rt-td.rthfc-td-fixed-left-last,
        .rt-tfooter .rt-td.rthfc-td-fixed-left-last {
            border-right: 1px solid ${colors.tableFixedBorderColor};
            padding-right: 16px;
        }

        .rt-thead .rt-th.rthfc-th-fixed-left-last + div.rt-th,
        .rt-tbody .rt-td.rthfc-td-fixed-left-last + div.rt-td,
        .rt-tfooter .rt-td.rthfc-td-fixed-left-last + div.rt-td {
            padding-left: 16px;
        }

        .rt-resizer {
            display: inline-block;
            width: 5px;
            height: 100%;
            position: absolute;
            right: 0;
            top: 0;
            transform: translateX(50%);
            z-index: 1;
            cursor: col-resize !important;

            &:before {
                content: ' ';
                position: absolute;
                left: 50%;
                transform: translateX(-50%);
                display: none;
                width: 6px;
                height: 100%;
                background: ${colors.tableResizerColorHover};
            }
        }

        .rt-resizer:active {
        }

        .rt-resizer:hover,
        .rt-resizer:active {
            &:before {
                display: block;
            }
        }
    }

    #table-history-inquiry.ReactTable {
        .rt-thead .rt-th,
        .rt-tbody .rt-td,
        .rt-tfooter .rt-td {
            padding-right: 8px;
        }
    }

    .sticky {
        position: unset !important;
        transform: unset !important;
    }

    @supports (position: sticky) {
        .ReactTable {
            margin-bottom: ${(props) => props.footerHeight}px;
            transition: margin 0.3s;

            .rt-table {
                position: relative;
                overflow: visible;

                .rt-tbody {
                    overflow: hidden;
                    min-width: auto !important;
                    height: auto;
                }
            }

            .rt-thead {
                top: 0;
                position: -webkit-sticky;
                position: sticky;
                min-width: auto !important;
                overflow: hidden;
            }

            .rt-tfooter {
                bottom: 96px;
                position: -webkit-sticky;
                position: sticky;
                min-width: auto !important;
                overflow: hidden;
            }

            .rt-tr {
                width: fit-content;
            }
        }

        .ReactTable.footer-close {
            margin-bottom: 55px;
            transition: margin 0.3s;
        }
    }

    &.scrolled .ReactTable .rt-thead .rt-th.rthfc-th-fixed-left-last,
    &.scrolled .ReactTable .rt-tbody .rt-td.rthfc-td-fixed-left-last,
    &.scrolled .ReactTable .rt-tfooter .rt-td.rthfc-td-fixed-left-last {
        border-right-color: transparent;
    }

    &.scrolled .rthfc .rt-th.rthfc-th-fixed-left-last,
    &.scrolled .rthfc .rt-td.rthfc-td-fixed-left-last {
        box-shadow: 1px 0 0 0 rgba(150, 150, 150, 0.25),
            2px 0 0 0 rgba(150, 150, 150, 0.2),
            3px 0 0 0 rgba(150, 150, 150, 0.15),
            4px 0 0 0 rgba(150, 150, 150, 0.1),
            5px 0 0 0 rgba(150, 150, 150, 0.05);
    }
`;

interface IHorizontalScrollBlockStyled {
    bottomPosition?: string;
    marginLeft: number | undefined;
    width: string;
}

const HorizontalScrollBlockStyled = styled.div.attrs({
    'className': 'table__horizontal-scroll-wrapper scroll',
    'data-horizontal-scroll': 'horizontalScroll',
})<IHorizontalScrollBlockStyled>`
    position: absolute;
    bottom: ${(props) => (props.bottomPosition ? props.bottomPosition : 0)};
    overflow: auto;
    ${(props) => (props.marginLeft ? `margin-left: ${props.marginLeft}px;` : '')}
    width: ${(props) => props.width};

    &::-webkit-scrollbar {
        height: 12px;
    }

    &::-webkit-scrollbar-track-piece:horizontal {
        background-color: #e5e5e5;
    }

    &::-webkit-scrollbar-thumb:horizontal {
        background: #b1b3b6 url(${icons.scrollbarThumbIcon}) no-repeat center;
        border-radius: 6px;
    }
`;

const HorizontalScrollBlockContainerStyled = styled(GlobalContainer)`
    position: relative;
`;

const HorizontalScrollStyled = styled.div.attrs({
    className: 'table__horizontal-scroll',
})`
    height: 1px;
`;

const TableFooterStyled = styled.div.attrs({
    className: 'table__table-footer fixed-with-scrollbar',
})`
    position: fixed;
    z-index: 5;
    left: 0;
    bottom: ${TABLE_FOOTER_BOTTOM_STYLE}px;
    width: 100%;
    background: ${colors.tableFooterBgColor};

    & .global-container {
        padding: 0 40px;
    }
`;

const TableFooterNavStyled = styled.div`
    width: 100%;
    min-width: ${globalThemeConstants.minSiteWidth};
    position: relative;
    content: ' ';
    box-shadow: 0 -4px 8px -3px rgba(0, 0, 0, 0.2);
`;

const FilterStubStyled = styled.div.attrs({
    className: 'table__filter-stub',
})`
    height: 32px;
    width: 100%;
`;

export const tableLoaderShimmer = keyframes`
    0% {
        background-position: -50px;
    }
    100% {
        background-position: 1150px;
    }
`;

interface ITableLoaderContainerStyled {
    isSticky: boolean;
}

const TableLoaderContainerStyled = styled.div<ITableLoaderContainerStyled>`
    position: ${(props) => (props.isSticky ? 'sticky' : 'absolute')};
    top: ${(props) => (props.isSticky ? '83' : '80')}px;
    height: 2px;
    width: 100%;
    overflow: hidden;
    backface-visibility: hidden;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    animation-fill-mode: forwards;
    animation-duration: 1.2s;
    animation-name: ${tableLoaderShimmer};
    background: ${colors.tableLoaderBackgroundGradient};
    z-index: 4;
`;

export const TableRemovedRowItemStyled = styled.div`
    position: absolute;
    z-index: 1;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    > span:first-child {
        flex: 1;
        text-align: center;
    }

    ${LinkContainerStyled} {
        margin-left: 8px;
    }
`;

export {
    FilterStubStyled,
    HorizontalScrollStyled,
    HorizontalScrollBlockStyled,
    HorizontalScrollBlockContainerStyled,
    TableContainerStyled,
    TableFooterStyled,
    TableFooterNavStyled,
    TableLoaderContainerStyled,
};
